// Recently uploaded workspace background images, newest first, capped at one grid row.
// Local-only history: the backend stores just the active background attachment, so the
// picker remembers previous uploads per workspace to offer them as quick re-picks.

export interface WorkspaceBackgroundHistoryEntry {
  id: string;
  url: string;
}

export const WORKSPACE_BACKGROUND_HISTORY_LIMIT = 4;

function historyKey(workspaceId: string): string {
  return `output:workspace-background-history:${workspaceId}`;
}

export function getWorkspaceBackgroundHistory(workspaceId?: string | null): WorkspaceBackgroundHistoryEntry[] {
  if (typeof window === 'undefined' || !workspaceId) return [];

  try {
    const raw = window.localStorage.getItem(historyKey(workspaceId));
    const parsed = raw ? (JSON.parse(raw) as WorkspaceBackgroundHistoryEntry[]) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry) => entry && typeof entry.id === 'string' && typeof entry.url === 'string');
  } catch {
    return [];
  }
}

// Adds a newly uploaded background to the front, dropping the oldest beyond the cap.
// Re-selecting an already-remembered image keeps slot order stable (no reshuffle).
export function rememberWorkspaceBackground(
  workspaceId: string | null | undefined,
  entry: WorkspaceBackgroundHistoryEntry,
): WorkspaceBackgroundHistoryEntry[] {
  const current = getWorkspaceBackgroundHistory(workspaceId);
  if (!workspaceId || !entry.id || !entry.url) return current;
  if (current.some((existing) => existing.id === entry.id)) return current;

  const next = [entry, ...current].slice(0, WORKSPACE_BACKGROUND_HISTORY_LIMIT);
  try {
    window.localStorage.setItem(historyKey(workspaceId), JSON.stringify(next));
  } catch {
    // History still applies to the current render when storage is unavailable.
  }
  return next;
}
